import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { useLang, route, servicePath } from "../i18n";
import { useCompany, useSiteServices } from "./SiteContext";
import { Logo } from "./Logo";
import { AviahWatermark } from "./AviahMark";
import { ButtonPrimary } from "./Buttons";
import { track } from "../lib/track";

const Footer = () => {
  const { lang, t } = useLang();
  const company = useCompany();
  const services = useSiteServices();
  const a = company.address;
  const year = new Date().getFullYear();
  const socials = Object.entries(company.social).filter(([, url]) => url);

  const columns = [
    {
      title: t.footer.company,
      links: [
        { label: t.footer.about, to: route(lang, "about") },
        { label: t.footer.founder, to: route(lang, "author") },
        { label: t.footer.contact, to: route(lang, "contact") },
      ],
    },
    {
      title: t.footer.services,
      links: services.map((s) => ({ label: s[lang].title, to: servicePath(lang, s.slug), testid: `footer-service-${s.slug}` })),
    },
    {
      title: t.footer.resources,
      links: [
        { label: t.footer.blog, to: route(lang, "blog") },
        { label: t.footer.resources, to: route(lang, "resources") },
        { label: t.footer.faq, to: route(lang, "faq") },
      ],
    },
    {
      title: t.footer.legal,
      links: [
        { label: t.footer.privacy, to: route(lang, "privacy") },
        { label: t.footer.terms, to: route(lang, "terms") },
        { label: t.footer.disclosures, to: route(lang, "disclosures") },
        { label: t.footer.accessibility, to: route(lang, "accessibility") },
        { label: t.footer.editorial, to: route(lang, "editorial") },
      ],
    },
  ];

  const brandBlock = (
    <div>
      <Logo variant="black" className="h-8 w-auto" testid="footer-logo" />
      <address className="mt-6 text-sm not-italic leading-relaxed text-[#171717]/60">
        {a.street}
        <br />
        {a.suite}
        <br />
        {a.city}, {a.state} {a.zip}
      </address>
      <a
        href={company.phoneHref}
        data-testid="footer-phone"
        onClick={() => track("phone_click", { location: "footer" })}
        className="mt-4 inline-flex min-h-[44px] items-center text-sm text-[#171717] transition-colors hover:text-[#8a6f3e]"
      >
        {company.phone}
      </a>
      {company.email && (
        <a href={`mailto:${company.email}`} className="mt-1 block text-sm text-[#171717]/70 hover:text-[#8a6f3e]">
          {company.email}
        </a>
      )}
      <p className="mt-3 text-xs uppercase tracking-[0.18em] text-[#8a6f3e]">
        {company.officeHours.days[lang]} · {company.officeHours.open}–{company.officeHours.close}
      </p>
      <p className="mt-1 text-xs text-[#171717]/45">{t.footer.appointment}</p>
      {socials.length > 0 && (
        <div className="mt-4 flex gap-4">
          {socials.map(([name, url]) => (
            <a key={name} href={url} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-[44px] items-center text-xs uppercase tracking-[0.15em] text-[#171717]/60 hover:text-[#8a6f3e]">
              {name}
            </a>
          ))}
        </div>
      )}
      <div className="mt-6">
        <ButtonPrimary to={route(lang, "contact")} testid="footer-cta" event="book_consultation_click">
          {t.footer.bookCta}
        </ButtonPrimary>
      </div>
    </div>
  );

  return (
    <footer className="relative overflow-hidden border-t border-[#171717]/10 bg-[#F4F0E8]" data-testid="site-footer">
      <AviahWatermark className="absolute -bottom-24 -right-24 h-[26rem] w-[26rem] text-[#171717] opacity-[0.04]" />
      <div className="relative mx-auto max-w-7xl px-5 py-16 sm:px-8 sm:py-24">
        <div className="max-w-3xl">
          {t.footer.statement.map((line, i) => (
            <p key={i} className="text-2xl font-light leading-snug tracking-tight text-[#171717] sm:text-3xl lg:text-4xl">
              {i === 1 ? <span className="text-[#8a6f3e]">{line}</span> : line}
            </p>
          ))}
        </div>

        {/* Desktop grid */}
        <div className="mt-16 hidden gap-12 border-t border-[#171717]/10 pt-14 lg:grid lg:grid-cols-5">
          {brandBlock}
          {columns.map((col) => (
            <div key={col.title}>
              <h3 className="eyebrow text-[#8a6f3e]">{col.title}</h3>
              <ul className="mt-5 space-y-3">
                {col.links.map((l) => (
                  <li key={l.to}>
                    <Link
                      to={l.to}
                      data-testid={l.testid}
                      className="inline-flex min-h-[32px] items-center text-sm text-[#171717]/65 transition-colors hover:text-[#8a6f3e]"
                    >
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Mobile: brand block + accordion navigation */}
        <div className="mt-14 border-t border-[#171717]/10 pt-10 lg:hidden">
          {brandBlock}
          <div className="mt-10">
            {columns.map((col) => (
              <details key={col.title} className="group border-b border-[#171717]/10">
                <summary className="flex min-h-[52px] cursor-pointer list-none items-center justify-between py-4 [&::-webkit-details-marker]:hidden">
                  <span className="eyebrow text-[#8a6f3e]">{col.title}</span>
                  <ChevronDown className="h-4 w-4 text-[#171717]/40 transition-transform duration-300 group-open:rotate-180" />
                </summary>
                <ul className="space-y-1 pb-5 pl-1">
                  {col.links.map((l) => (
                    <li key={l.to}>
                      <Link
                        to={l.to}
                        data-testid={l.testid}
                        className="inline-flex min-h-[44px] items-center text-sm text-[#171717]/70 transition-colors hover:text-[#8a6f3e]"
                      >
                        {l.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </details>
            ))}
          </div>
        </div>

        <div className="mt-16 border-t border-[#171717]/10 pt-8">
          <p className="max-w-3xl text-[0.7rem] leading-relaxed text-[#171717]/40">{t.footer.miniDisclaimer}</p>
          <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-[#171717]/45">
              © {year} {company.name}. {t.footer.rights}{" "}
              <span className="text-[#171717]/35">{t.footer.poweredBy}{" "}</span>
              <a
                href="https://www.eternitymp.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-eternity-mp"
                className="text-[#8a6f3e] underline-offset-4 transition-colors duration-300 hover:text-[#C6A46A] hover:underline"
              >
                Eternity MP
              </a>
            </p>
            <Link
              to="/admin"
              data-testid="footer-admin-link"
              className="inline-flex min-h-[44px] w-fit items-center text-[0.58rem] font-normal uppercase tracking-[0.24em] text-[#171717]/35 transition-colors duration-300 hover:text-[#8a6f3e]"
            >
              Admin
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
